import CSS from "./static/css/loginInputContainer.module.css";
import { FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { customToast } from "../../../../Common/ts/commonTools";
import { deleteAllCookies, setCookie, webViewBridgeClass } from "../../../../Common/ts/appTools";
import { MystudioApiError, mystudioV4 } from "../../../../Common/api/v4/client";
import { isPasswordlessDemoLoginAllowed, isPublicPasswordlessDemoSession } from "../../../../Common/api/v4/previewGuard";
import LoopAnimationModal from "../../../../Component/Elements/LoopAnimationModal/LoopAnimationModal";
import { setLocalPreviewLoginCookies } from "../../localPreviewLogin";
import loginCharacter from "../../static/img/figma-login-character.png";
import loginLogoCopy from "../../static/img/figma-login-logo-copy.png";

const webViewBridge = new webViewBridgeClass();

const LoginInputContainer = () => {
    const canUsePasswordlessDemoLogin = isPasswordlessDemoLoginAllowed();
    const isPublicDemo = isPublicPasswordlessDemoSession({ token: "preview" });
    const [id, setId] = useState("");
    const [password, setPassword] = useState("");
    const [isRememberedId, setIsRememberedId] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const id_input = useRef<HTMLInputElement>(null);
    const password_input = useRef<HTMLInputElement>(null);

    async function requestLogin({ id, password }: { id: string; password: string }) {
        return mystudioV4.login(id, password);
    }

    useEffect(() => {
        const localStorage_is_remembered_id = localStorage.getItem("isRememberedId");
        if(localStorage_is_remembered_id === "true"){
            const localStorage_username = localStorage.getItem("username");
            if(localStorage_username){
                setIsRememberedId(true);
                setId(localStorage_username);
            }
        }else{
            setIsRememberedId(false);
        }
    }, []);

    const printError = (error: string) => {
        customToast.error({
            msg: error,
            toastId: "login_fail"
        });
    };

    const loginAction = () => {
        if (id.length < 1) {
            id_input.current?.focus();
            printError("아이디를 입력해 주세요.");
        } else if (password.length < 1) {
            password_input.current?.focus();
            printError("비밀번호를 입력해 주세요.");
        } else {
            setIsLoading(true);
            requestLogin({id, password})
                .then((response) => {
                    if(!response.data.accessToken){
                        printError("로그인 응답을 확인할 수 없습니다.");
                        return;
                    }

                    let days = 0;
                    const m_isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

                    if(m_isMobileDevice && webViewBridge.flutterInAppWebView){
                        days = 90;
                    }

                    setCookie({name:"token", value: response.data.accessToken, days});

                    if(isRememberedId){
                        localStorage.setItem("isRememberedId", "true");
                        localStorage.setItem("username", id);
                    }else{
                        localStorage.removeItem("isRememberedId");
                        localStorage.removeItem("username");
                    }

                    window.location.replace("/tabs/home");
                })
                .catch((error) => {
                    if(error instanceof MystudioApiError && error.status === 401){
                        printError("아이디 혹은 비밀번호를 확인해 주세요.");
                    }else if(error instanceof MystudioApiError && error.status === 429){
                        printError("로그인 시도가 많습니다. 잠시 후 다시 시도해 주세요.");
                    }else{
                        printError("로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.");
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const localPreviewLoginAction = () => {
        deleteAllCookies();
        setLocalPreviewLoginCookies();
        window.location.replace("/tabs/home");
    };

    const submitLogin = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if(canUsePasswordlessDemoLogin){
            localPreviewLoginAction();
            return;
        }

        loginAction();
    };

    const socialLoginHandler = (provider: string) => {
        customToast.info({
            msg: `${provider} 로그인은 준비 중입니다.`,
            toastId: `social-login-${provider}`,
        });
    };

    const readyToastHandler = (label: string) => {
        customToast.info({
            msg: `${label}은 준비 중입니다.`,
            toastId: `ready-${label}`,
        });
    };

    return (
        <div className={CSS.l_LoginInputContainer_main}>
            <div className={CSS.l_LoginInputContainer_statusBar} aria-hidden="true">
                <span>9:41</span>
                <span className={CSS.l_LoginInputContainer_statusIcons}>
                    <span />
                    <span />
                    <span />
                </span>
            </div>

            <img
                className={CSS.l_LoginInputContainer_logoCopy}
                src={loginLogoCopy}
                alt="촬영 일정관리, 사진 관리, 제품 제작까지 마이 스튜디오 하나로"
            />

            <div className={CSS.l_LoginInputContainer_characterWrap}>
                <img src={loginCharacter} alt="럽맘 스튜디오 캐릭터" />
                <span className={CSS.l_LoginInputContainer_helloBubble}>만나서 반가워요!</span>
            </div>

            <form
                className={`${CSS.l_LoginInputContainer_form} ${canUsePasswordlessDemoLogin ? CSS.l_LoginInputContainer_demoForm : ""}`}
                onSubmit={submitLogin}
            >
                {canUsePasswordlessDemoLogin ? (
                    <div id="demo-login-description" className={CSS.l_LoginInputContainer_demoDescription}>
                        <strong>로그인 한 번으로 시작하세요</strong>
                        <p>아이디·비밀번호 없이 앱을 둘러보세요.</p>
                        {isPublicDemo && <p>공유 데모에서는 예시 콘텐츠만 제공하며,<br />실제 고객 정보는 조회·변경되지 않습니다.</p>}
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            className={CSS.l_LoginInputContainer_autoLogin}
                            onClick={() => setIsRememberedId(!isRememberedId)}
                            aria-pressed={isRememberedId}
                        >
                            <span>자동 로그인</span>
                            <span className={CSS.l_LoginInputContainer_helpIcon}>!</span>
                        </button>

                        <input
                            className={CSS.l_LoginInputContainer_input}
                            type="text"
                            value={id}
                            onChange={(e) => setId(e.target.value)}
                            ref={id_input}
                            placeholder="아이디를 입력해 주세요"
                            aria-label="아이디"
                            autoComplete="username"
                            autoCapitalize="none"
                        />

                        <input
                            className={CSS.l_LoginInputContainer_input}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            ref={password_input}
                            placeholder="비밀번호를 입력해 주세요"
                            aria-label="비밀번호"
                            autoComplete="current-password"
                        />
                    </>
                )}

                <button
                    className={CSS.l_LoginInputContainer_loginBtn}
                    type="submit"
                    disabled={isLoading}
                    aria-describedby={canUsePasswordlessDemoLogin ? "demo-login-description" : undefined}
                >
                    로그인
                </button>

            </form>

            {!canUsePasswordlessDemoLogin && (
                <>
                    <div className={CSS.l_LoginInputContainer_snsDivider}>
                        <span />
                        <p>SNS 계정으로 로그인</p>
                        <span />
                    </div>

                    <div className={CSS.l_LoginInputContainer_socialArea}>
                        <div className={CSS.l_LoginInputContainer_recentBadge}>최근 로그인</div>
                        <button type="button" className={CSS.l_LoginInputContainer_naverBtn} onClick={() => socialLoginHandler("네이버")} aria-label="네이버 로그인">N</button>
                        <button type="button" className={CSS.l_LoginInputContainer_kakaoBtn} onClick={() => socialLoginHandler("카카오")} aria-label="카카오 로그인">K</button>
                        <button type="button" className={CSS.l_LoginInputContainer_googleBtn} onClick={() => socialLoginHandler("구글")} aria-label="구글 로그인">G</button>
                    </div>

                    <div className={CSS.l_LoginInputContainer_links}>
                        <Link to="/resetUserPassword">아이디,비밀번호 찾기</Link>
                        <button type="button" onClick={() => readyToastHandler("회원가입")}>회원가입</button>
                    </div>
                </>
            )}

            <LoopAnimationModal isModalOpen={isLoading} />
        </div>
    );
};

export default LoginInputContainer;
